import _ from 'lodash';
import { MoUtils } from 'ui-common';
import { BeaconConfig, BeaconEmbeddableActivityProps, BeaconPagingValue, MergedBeaconConfig, SavedBeaconConfig, TextOperator, TextSelectorMatch } from './interfaces';
import { v4 } from 'uuid';
import { EmbedsConfigApiService } from '../embeds-config-api-service';
import { extractElementValue } from '../../services/embeddable-helper';
import { safeFunctionEval } from '../../utils/safe-function-eval';

export const beaconSignalTypes = ['query', 'click', 'cartAdd', 'purchase'];

export const defaultSiteVisitThreshold = 30 * 60 * 1000;
export const defaultDelayForPurchaseRedirect = 1000;
export const defaultDelayForProductViewRedirect = 2000;

export const searchPageType = "SEARCH";
export const unmatchedPageType = "OTHER";

const configCacheExpirationMins = 5;

// TODO: Replace all function arguments of `logVerboseInfo` with `this` in a housekeeping story.
const logVerboseInfo: boolean = localStorage.enableBeaconDev === 'verbose';

export const defaultConfig: BeaconConfig = {
  attributes: {
    productPath: '.lw-product-item',
    cartId: 'lwRetailCartId',
    idQueryParam: 'pid',
    numberTypeFields: ['price', 'quantity', 'revenue'],

    // The session timeout threshold in milliseconds.
    // By default, it is set to 30 minutes (30 minutes * 60 seconds * 1000 milliseconds).
    sessionTimeoutThreshold: 30 * 60 * 1000,
  },

  fields: {
    productId: ':scope @id',
  },

  query: {
    queryParam: 'query',
    delayForTrackQueryUpdate: 1000,
    sendSignalDebounceDelay: 200,
  },

  click: {
    // By default, these attributes inherit from the top-level attributes [productPath|fields].
  },

  cartAdd: {
    trigger: `.lw-cart-add-trigger, button[textContent='Add to Cart']`,
  },

  purchase: {
    trigger: '.lw-purchase-trigger',
  },
};

export const getBeaconConfig = (customConfig: BeaconConfig = {}): MergedBeaconConfig => {
  const mergedConfig = _.defaultsDeep(customConfig, defaultConfig);

  // Apply the global id attribute and fields to each specific type of signal config.
  _.each(beaconSignalTypes, (type) => {
    mergedConfig[type].productPath ??= mergedConfig.attributes.productPath;
    mergedConfig[type].fields = _.defaultsDeep(mergedConfig[type].fields || mergedConfig.fields, mergedConfig.fields);
  });

  if (mergedConfig?.visit?.enabled) {
    mergedConfig.visit.threshold ??= defaultSiteVisitThreshold;
  }

  if(mergedConfig?.purchase_complete && mergedConfig?.purchase_complete?.enabled !== false) {
    mergedConfig.purchase_complete.delayForPurchaseRedirect ??= defaultDelayForPurchaseRedirect;
  }

  if (mergedConfig?.embeddable_activity?.enabled) {
    mergedConfig.embeddable_activity.delayForRedirect ??= defaultDelayForProductViewRedirect;
  }

  return mergedConfig;
};

const getProductData = (element, beaconConfig) => {
  return MoUtils.safeParseJSON(element.getAttribute(beaconConfig.attributes.data) || {});
};

/**
 * Process the field value based on the field configuration.
 */
const processFieldValue = (field, origValue, fieldConfig, beaconConfig, element) => {
  const numberTypeFields = beaconConfig.attributes.numberTypeFields;
  let convertToNumber = _.includes(numberTypeFields, field);
  let value = origValue;

  if (_.isPlainObject(fieldConfig)) {
    if (fieldConfig.type === 'number') {
      convertToNumber = true;
    }

    if (fieldConfig.exclude && value) {
      const isPatternRegExp = isRegExpPattern(fieldConfig.exclude);

      if (isPatternRegExp) {
        const regexp = _.trim(fieldConfig.exclude, '/');

        value = _.nth(value.match(regexp), 1)
      } else {
        value = value.replace(fieldConfig.exclude, '');
      }
    }

    if (fieldConfig.queryParamFromUrl && value) {
      try {
        const urlObj = new URL(value);

        value = urlObj.searchParams.get(fieldConfig.queryParamFromUrl);
      } catch (e) {
        // Ignore the error from parsing URL and keep the original value.
      }
    }

    if (fieldConfig.jsFunction) {
      let valueGetter;

      try {
        valueGetter = safeFunctionEval(fieldConfig.jsFunction);
      } catch (error) {
        if (logVerboseInfo) {
          console.warn('Failed to parse JS function from Beacon Config:\n', 'Field:', field, '\n Function String:', fieldConfig.jsFunction, '\n Error Details:', error);
        }
      }

      if (_.isFunction(valueGetter)) {
        // Loosen the restriction by passing `window` as the main context, since `safe-eval` runs in its own sandbox to prevent malicious code from accessing the browser's document.
        value = valueGetter.call(this, {
          origValue: _.isString(value) ? value : '',
          window, field, fieldConfig, beaconConfig, element,
        }) || value;
      }
    }
  }

  if (value) {
    value = _.trim(value);
  }

  if (convertToNumber && value) {
    const trimmedValue = value.replaceAll(',', '');
    const numericStr = /\d+(\.\d+)?/.exec(String(trimmedValue)) || [];

    value = parseFloat(numericStr[0] || '');
  }

  return value;
};

/**
 * Get the value from the element based on the target path and field configuration.
 */
const getValueFromElement = (field, fieldConfig, targetPath, element, beaconConfig) => {
  const rawValue = extractElementValue(targetPath, element);

  return processFieldValue(field, rawValue, fieldConfig, beaconConfig, element);
};

export const getCartId = (config: MergedBeaconConfig) => {
  let storedCartId = localStorage[config.attributes.cartId];

  if (!storedCartId) {
    storedCartId = localStorage[config.attributes.cartId] = v4();
  }

  return storedCartId;
};

export const clearStoredCartId = (config: MergedBeaconConfig) => {
  delete localStorage[config.attributes.cartId];
};

const executePaginationHandler = (handlerString: string, pagingValue: BeaconPagingValue): number | null => {
  try {
    const handlerFn = safeFunctionEval(handlerString);

    if (typeof handlerFn === 'function') {
      const value = handlerFn.call(this, { window, pagingValue, MoUtils });
      const result = typeof value === 'number' ? value : Number.parseInt(value, 10);

      return Number.isNaN(result) ? null : result;
    }
  } catch (e) {
    if (logVerboseInfo) {
      console.warn(`Error executing pagination handler "${handlerString}":`, e);
    }
  }

  return null;
};

/**
 * Extracts the numeric value from a BeaconPagingValue configuration.
 * Supports: direct number, query param string, or object with queryParam/handler.
 */
export const getPaginationValue = (pagingValue: BeaconPagingValue): number | null => {
  if (typeof pagingValue === 'number') {
    return pagingValue;
  } else if (pagingValue && 'queryParam' in pagingValue) {
    // Object with queryParam property
    const value = MoUtils.getUrlQueryParams(pagingValue.queryParam);
    const parsed = value ? Number.parseInt(value, 10) : null;

    return Number.isNaN(parsed) ? null : parsed;
  } else if (pagingValue && 'handler' in pagingValue) {
    // Object with handler property (custom function)
    return executePaginationHandler(pagingValue.handler, pagingValue);
  }

  return null;
};

/**
 * Calculates the absolute click position across paginated results.
 */
export const getClickPosition = (indexOnPage: number, beaconConfig?: any): number => {
  let selectedPosition = indexOnPage + 1;
  const paginationConfig = beaconConfig?.pagination;

  if (paginationConfig && indexOnPage >= 0) {
    const rpp = getPaginationValue(paginationConfig.rpp);
    const page = getPaginationValue(paginationConfig.page);

    if (rpp !== null && page !== null) {
      const pageBase = paginationConfig.pageBase ?? 1;

      selectedPosition = (page - pageBase) * rpp + (indexOnPage + 1);
    }
  }

  return selectedPosition;
};

export const isRegExpPattern = (str: string): boolean => {
  return /^\/.*\/[gimsuy]*$/.test(str);
};

export const getFieldValue = (element, path, field, fieldConfig, beaconConfig) => {
  const isRichConfig = _.isPlainObject(path);
  const targetPath = isRichConfig ? path.path : path;
  let value;

  if (targetPath.includes('json:')) {
    const data = getProductData(element, beaconConfig);

    value = _.get(data.fields, value) || '';
  } else {
    value = getValueFromElement(field, fieldConfig, targetPath, element, beaconConfig);
  }

  return value;
};

const separateFacetValues = (facetValues: string, valuesDelimiter: string) => {
  return valuesDelimiter === '+' ? _.split(facetValues, ' ') : _.split(facetValues, decodeURIComponent(valuesDelimiter));
}

export const extractFacetValueUsingRegex = (value: string, jsonRegex: string) => {
  if (!isRegExpPattern(jsonRegex)) {
    return value;
  }
  const regex = jsonStringToRegex(jsonRegex);
  const match = value.match(regex);
  return match?.[1] ?? value;
}

export const jsonStringToRegex = (jsonString: string): RegExp => {
  return new RegExp(jsonString.replace(/(^\/|\/$)/g, ''));
}; 

export const getFacets = (config: MergedBeaconConfig, logVerboseInfo: boolean) => {
  const facetsConfig = config.query.facets;
  const excludeFacetsThatContain = _.get(facetsConfig, 'excludeFacetsThatContain');
  const facetKeyValuePairs = {}; 

  try {
    if (_.isArray(facetsConfig.queryParams)) { 
      _.forEach(facetsConfig.queryParams, (facetKey) => { 
        const facetValues = MoUtils.getUrlQueryParams(facetKey); 

        if(facetValues && !_.includes(excludeFacetsThatContain, facetKey)) {
          const separatedFacetValues = separateFacetValues(facetValues, facetsConfig.valuesDelimiter);
          const valueExtractorRegex = facetsConfig.valueExtractorRegex;

          if (facetsConfig.valueExtractorRegex) {
            facetKeyValuePairs[facetKey] = _.map(separatedFacetValues, (value) => extractFacetValueUsingRegex(value, valueExtractorRegex));
          } else {
            facetKeyValuePairs[facetKey] = separatedFacetValues;
          }
        }
      });
    } else { // repeated url param
      const groupedFacets = MoUtils.getUrlQueryParams(facetsConfig.queryParams);
      if (!groupedFacets) {
        return [];
      }

      let separatedFacets = [];

      // if the facet delimiter is provided, split the grouped facets by the delimiter
      if (facetsConfig.facetDelimiter) {
        separatedFacets = _.split(groupedFacets, decodeURIComponent(facetsConfig.facetDelimiter));
      } else {
        separatedFacets = _.castArray(groupedFacets);
      }

      // split the facet key and value/s and exclude appropriate facets
      _.forEach(separatedFacets, (facet) => {
        let [facetName, rawFacetValue] = _.split(facet, decodeURIComponent(facetsConfig.keyValueDelimiter));
        let facetValues = [rawFacetValue];

        if (_.includes(excludeFacetsThatContain, facetName)) {
          return;
        } 

        // separate the facet values if the values delimiter is provided
        if (facetsConfig.valuesDelimiter) {
          facetValues = separateFacetValues(rawFacetValue, facetsConfig.valuesDelimiter);
        }

        // extract the facet value using the regex provided
        if (facetsConfig.valueExtractorRegex) {
          facetValues = _.map(facetValues, (value) => extractFacetValueUsingRegex(value, facetsConfig.valueExtractorRegex));
        }

        facetKeyValuePairs[facetName] = _.union(facetKeyValuePairs[facetName] || [], facetValues);
      })
    }
  } catch (e) {
    if (logVerboseInfo) {
      console.warn(e);
    }
    return [];
  }
  return _.map(facetKeyValuePairs, (values, name) => ({ name: name, values: values }));
}

export const facetsConfigIsValid = (config: MergedBeaconConfig, logVerboseInfo: boolean) => {
  const facetsConfig = config.query.facets;
  const queryParams = facetsConfig.queryParams;

  if (queryParams) {
    if (_.isArray(queryParams) && !facetsConfig.valuesDelimiter) {
      if(logVerboseInfo) {
        console.warn('Facet values delimiter is not provided.');
      }
      return false;
    } else if(_.isString(queryParams) && !facetsConfig.keyValueDelimiter) {
        if(logVerboseInfo) {
          console.warn('Key value delimiter is not provided.');
        }
      return false;
    }
  }
  return true;
}

const getInjectedBeaconScript = () => document.querySelector('#lw-ui-lib[beacon][mode=injected]');


export const getSavedBeaconConfig = async (configApiService: EmbedsConfigApiService) => {
  const injectedBeaconScript = getInjectedBeaconScript();
  const configKey = `lw${injectedBeaconScript ? 'Injected' : ''}BeaconConfig`;
  const cachedTimeKey = `lw${injectedBeaconScript ? 'Injected' : ''}BeaconConfigCacheTime`;
  let savedConfig: SavedBeaconConfig = {};
  let cachedConfigTime: Date;

  savedConfig = MoUtils.safeParseJSON(localStorage.getItem(configKey));
  cachedConfigTime = localStorage.getItem(cachedTimeKey) ? new Date(Number(localStorage.getItem(cachedTimeKey))) : null;

  if (!savedConfig || !cachedConfigTime) {
    savedConfig = await fetchBeaconConfig(configApiService);
    if (savedConfig) {
      cacheBeaconConfig(savedConfig);
    }
  } else {
    const now = new Date();
    const minuteDiff = (now.getTime() - cachedConfigTime.getTime()) / (1000 * 60);
    if (minuteDiff > configCacheExpirationMins) {
      savedConfig = await fetchBeaconConfig(configApiService);
      if (savedConfig) {
        cacheBeaconConfig(savedConfig);
      }
    }
  }
  return savedConfig;
}

// Attempt to load the persisted Beacon configuration and overlay it on top of the
// legacy hardcoded configuration from the HTML <script> tag.
export const fetchBeaconConfig = async (configApiService: EmbedsConfigApiService) => {
  try {
    return await configApiService.invoke('config', 'getBeaconConfig');
  } catch (error) {
    if (logVerboseInfo) {
      console.warn('Failed to load the persisted Beacon Config');
    }
  }
}

export const cacheBeaconConfig = (beaconConfig: SavedBeaconConfig) => {
  const injectedBeaconScript = getInjectedBeaconScript();
  const configKey = `lw${injectedBeaconScript ? 'Injected' : ''}BeaconConfig`;
  const cachedTimeKey = `lw${injectedBeaconScript ? 'Injected' : ''}BeaconConfigCacheTime`;
  localStorage.setItem(configKey, JSON.stringify(beaconConfig));
  localStorage.setItem(cachedTimeKey, new Date().getTime().toString());
}

/**
 * Updates a specific path in the local beacon config for dev/testing purposes.
 * @param path - Path to the config property (e.g., 'pagination.rpp')
 * @param value - Value to set at the specified path
 * @example `setLocalBeaconConfig('pagination.rpp', { queryParam: 'page3' })`
 */
export const setLocalBeaconConfig = (path: string, value: any): any => {
  const beaconConfig = JSON.parse(localStorage.lwBeaconConfig);

  _.set(beaconConfig.config, path, value);
  localStorage.lwBeaconConfig = JSON.stringify(beaconConfig);

  return beaconConfig.config;
};

export const isOptionalProductProp = (field: string): boolean => {
  const optionalProps = ['price', 'quantity'];
  return optionalProps.includes(field);
}

export const getCustomSignalHandlers = (beaconConfig: MergedBeaconConfig) => {
  const signalHandlers = {};
  const signalTypes = ['query', 'click', 'cartAdd', 'purchase'];
  _.forEach(signalTypes, (signalType) => {
    if (!beaconConfig[signalType]?.handler) {
      return;
    }
    
    try {
      const signalHandler = safeFunctionEval(beaconConfig[signalType].handler);
      if (_.isFunction(signalHandler)) {
        signalHandlers[signalType] = (payload) =>
          signalHandler.call(this, {
            window,
            payload,
          });
      }
    } catch (e) {
      if (logVerboseInfo) {
        console.warn('Failed to parse signal handler from Beacon Config:\n', 'Signal Type:', signalType, '\n Function String:', beaconConfig[signalType].handler);
      }
    }
  });
  return signalHandlers;
}


/**
 * Parses a text-based selector string into its component parts.
 *
 * @param selector - The selector string to parse (e.g., "button[textContent*='Add']")
 * @returns Parsed match object or null if not a text-based selector
 */
const parseTextSelector = (selector: string): TextSelectorMatch | null => {
  // Pattern to match text-based selectors.
  // Captures: baseSelector, textContent attribute, operator, quote style, and search value.
  const regexp = /^((?:.|\s)*?)\[(textContent)([*^$]?)=(['"]?)(.*?)\4?\]$/;
  const match = regexp.exec(selector);

  if (!match) {
    return null;
  }

  const baseSelector = match[1] || '*';
  const operator = (match[3] || '=') as TextOperator;
  const searchValue = match[5];

  return { baseSelector, operator, searchValue };
};

/**
 * Queries elements with a text-based selector.
 *
 * @param parsedSelector - The parsed text selector
 * @param root - The root element to query from
 * @param caseInsensitive - Whether to perform case-insensitive matching
 * @returns Array of matching elements
 */
const queryWithTextSelector = (
  parsedSelector: TextSelectorMatch,
  root: Document | Element,
  caseInsensitive: boolean
): Element[] => {
  const { baseSelector, operator, searchValue } = parsedSelector;
  const normalizeText = (text: string): string => caseInsensitive ? text.toLowerCase() : text;
  const normalizedSearchValue = normalizeText(searchValue);
  const baseElements = Array.from(root.querySelectorAll(baseSelector));

  const matchesText = (element: Element) => {
    const getElementText = (element: Element): string => (element.textContent || '').trim();
    const normalizedElementText = normalizeText(getElementText(element));

    return operator === '*' ? normalizedElementText.includes(normalizedSearchValue)
         : operator === '^' ? normalizedElementText.startsWith(normalizedSearchValue)
         : operator === '$' ? normalizedElementText.endsWith(normalizedSearchValue)
         : normalizedElementText === normalizedSearchValue;
  };

  return baseElements.filter(matchesText);
};

/**
 * Recursively collects all shadow roots in the document tree.
 *
 * @param root - The root element to start searching from (defaults to document)
 * @returns Array of all shadow roots found
 */
const getAllShadowRoots = (root: Document | Element = document): ShadowRoot[] => {
  const shadowRoots: ShadowRoot[] = [];
  const stack: any[] = [root];

  while (stack.length) {
    const current = stack.pop();

    if (current?.shadowRoot) {
      shadowRoots.push(current.shadowRoot);
      stack.push(current.shadowRoot);
    }

    [...(current?.children ?? [])].forEach(child => stack.push(child));
  }

  return shadowRoots;
};

/**
 * Query elements on a single root with support for text-based selectors.
 *
 * @param selector - CSS selector(s) with optional text filters
 * @param root - Root element to query from
 * @param caseInsensitive - Whether text matching should be case-insensitive
 * @returns Array of matching elements
 */
const querySelectorOnRoot = (
  selector: string,
  root: Document | Element | ShadowRoot,
  caseInsensitive: boolean
): Element[] => {
  const hasTextFilter = selector.includes('textContent');
  const fallback = () => Array.from(root.querySelectorAll(_.isEmpty(selector) ? null : selector));

  if (hasTextFilter) {
    const selectors = selector.split(',').map((str) => str.trim()).filter(Boolean);

    return _(selectors).flatMap((sel) => {
      const parsed = parseTextSelector(sel);
      return parsed ? queryWithTextSelector(parsed, root as Document | Element, caseInsensitive) : fallback();
    }).value();
  } else {
    return fallback();
  }
};

/**
 * Enhanced querySelectorAll with extended selector support.
 *
 * Supports mixing regular CSS selectors with custom text-based selectors.
 * Multiple selectors can be comma-separated, just like standard querySelectorAll.
 *
 * Text-based selector syntax:
 *  - [textContent='foo']   exact match
 *  - [textContent*='foo']  contains
 *  - [textContent^='foo']  starts with
 *  - [textContent$='foo']  ends with
 *
 * Text matching is case-insensitive by default and uses textContent.
 *
 * Automatically searches all shadow roots, including nested shadow DOM.
 *
 * Examples:
 *  // Single text selector
 *  enhancedQuerySelector("button[textContent='Add to cart']");
 *
 *  // Mixed regular and text selectors
 *  enhancedQuerySelector("lw-breadcrumbs, [textContent='foo'], span, [textContent='bar']");
 *
 *  // Case-sensitive matching
 *  enhancedQuerySelector("div[textContent*='Sale']", document, { caseInsensitive: false });
 *
 * @param selector - CSS selector(s) with optional enhanced filters (comma-separated)
 * @param root - Root element to query from (defaults to document)
 * @param options - Query options for enhanced matching behavior
 *
 * @returns Array of unique matching elements
 */
export const enhancedQuerySelector = (
  sourceSelector: string,
  root: Document | Element = document,
  { caseInsensitive = true } = {}
): Element[] => {
  const selector = sourceSelector || '';
  const shadowRoots = getAllShadowRoots(root);
  const allRoots = [root, ...shadowRoots];
  const allResults = _.flatMap(allRoots, (currentRoot) => querySelectorOnRoot(selector, currentRoot, caseInsensitive));

  return _.uniq(allResults);
};

export const getProductIdUsingEmbedActivityConfig = (config: BeaconEmbeddableActivityProps) => {
  const { productIdSelector, productIdUrlParam } = config || {};
  let productId: string;
  if (productIdUrlParam) {
    productId = MoUtils.getUrlQueryParams(productIdUrlParam);
  } else if (productIdSelector) {
    productId = extractElementValue(productIdSelector, document);
  }
  return productId;
}

export const isProductPage = (config: BeaconEmbeddableActivityProps): boolean => {
  const productPagePattern = config?.productPagePattern;
  if (!productPagePattern) return false;

  const url = MoUtils.getWindowTop().document.location.href;

  if (isRegExpPattern(productPagePattern)) {
    // Regex pattern - strip leading/trailing slashes and match
    return !!url.match(productPagePattern.replace(/(^\/|\/$)/g, ''));
  }

  // Simple substring match
  return _.includes(url, productPagePattern);
}

export const getAgentTypeFromEmbed = (element: Element): string => {
  if (!element) return null;

  switch (element.tagName) {
    case 'AS-QNA-CONVERSATIONAL': {
      const container = element.querySelector('.as-qna-conversational-container');
      if (container?.hasAttribute('data-has-faq')) {
        return 'conversational_qa_faq';
      }
      return 'conversational_qa';
    }
    case 'AS-QNA':
      return 'product_qa';
    default:
      return null;
  }
}

export const AGENT_SELECTORS = 'AS-QNA-CONVERSATIONAL, AS-QNA';
